// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { XProductModel } from "@eisinc/Overrides/Pages/ProductDetailsPage";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import ReactImageMagnify from "react-image-magnify";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    image: ImageModel;
    useLargeImage?: boolean;
    onClick?: () => void;
    extendedStyles?: ProductPrimaryImageStyles;
    // xng-change
    isMagnifierEnabled?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

// xng-change: Add not found image & isDesktop.
const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const { productSettings, catalogSettings: { notFoundLargeImagePath, notFoundMediumImagePath } = {} as any } =
        getSettingsCollection(state);

    const notFoundImage = !props.useLargeImage
        ? notFoundMediumImagePath
        : notFoundLargeImagePath || notFoundMediumImagePath;

    return {
        productSettings: getSettingsCollection(state).productSettings,
        // xng-change
        isDesktopScreen: !(state as CustomAppState).xCommon.isTabletScreen,
        notFoundImage,
    };
};

export interface ProductPrimaryImageStyles {
    centeringWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    hiddenWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    spinner?: LoadingSpinnerProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
}

export const productPrimaryImageStyles: ProductPrimaryImageStyles = {
    centeringWrapper: {
        css: css`
            min-height: 300px;
            margin: 10px 0;
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                height: auto;
                max-width: 100%;
            }
        `,
    },
    image: {
        css: css`
            margin: auto;
            cursor: pointer;
        `,
    },
    img: {
        css: css`
            cursor: pointer;
        `,
    },
};

const ProductPrimaryImage = ({
    productSettings,
    product,
    image,
    useLargeImage,
    onClick,
    extendedStyles,
    // xng-change
    isDesktopScreen,
    isMagnifierEnabled,
    notFoundImage,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productPrimaryImageStyles, extendedStyles));
    // <xng-change>
    const [imageSrc, setImageSrc] = useState<ImageModel>(image);
    const [errorLoadingImage, setErrorLoadingImage] = useState(true);

    const isLoading = (product as XProductModel)?.isLoading;
    // </xng-change>

    useEffect(() => {
        if ((window as any).sirvScriptAdded) {
            return;
        }
        if (
            productSettings.imageProvider !== "SIRV" ||
            !product.images ||
            product.images.every(o => o.imageType !== "360")
        ) {
            return;
        }

        const script = document.createElement("script");
        script.src = "https://scripts.sirv.com/sirv.js";
        script.async = true;

        document.body.appendChild(script);
        (window as any).sirvScriptAdded = true;
    }, [productSettings.imageProvider, product]);

    // <xng-change>
    useEffect(() => {
        setImageSrc(image);
        setErrorLoadingImage(false);
    }, [image]);

    // XNG-Change
    useEffect(() => {
        if (!product?.images?.length) {
            setErrorLoadingImage(true);
        }
    }, [product]);

    const handleNotFoundImage = (isSmallImage?: boolean) => {
        setErrorLoadingImage(true);
        const imagePathKey = isSmallImage ? "mediumImagePath" : "largeImagePath";

        setImageSrc(prevState => ({
            ...prevState,
            [imagePathKey]: notFoundImage || "",
        }));
    };
    // </xng-change>

    const imageWrapperClickHandler = () => {
        onClick?.();
    };

    const imageWrapperKeyHandler: React.KeyboardEventHandler<HTMLImageElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            onClick?.();
        }
    };

    const path = useLargeImage
        ? image.largeImagePath || image.mediumImagePath
        : image.mediumImagePath || image.largeImagePath;

    // XNG-Change : add skeleton
    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <Skeleton />
            </StyledWrapper>
        );
    }

    // xng-change: Enable fallback image if any.
    if ((!path && !notFoundImage) || !product) {
        return null;
    }

    // <xng-changes>
    if (isMagnifierEnabled && isDesktopScreen && path && !!product?.images?.length && !errorLoadingImage) {
        const width = 882;
        const height = 687;
        return (
            <StyledWrapper {...styles.centeringWrapper} className={errorLoadingImage ? "has-error" : ""}>
                <ReactImageMagnify
                    {...{
                        smallImage: {
                            alt: imageSrc.imageAltText,
                            isFluidWidth: true,
                            src: imageSrc.mediumImagePath,
                            onError() {
                                handleNotFoundImage(true);
                            },
                        },
                        largeImage: {
                            src: imageSrc.largeImagePath,
                            width: width * 1.5,
                            height: height * 1.5,
                            onError() {
                                handleNotFoundImage();
                            },
                        },
                        enlargedImageContainerDimensions: {
                            width,
                            height,
                        },
                        enlargedImageContainerClassName: "enlarged-img",
                        enlargedImageContainerStyle: {
                            left: "339px",
                        },
                    }}
                />
            </StyledWrapper>
        );
    }
    // </xng-changes>

    return (
        <>
            {image.imageType === "Static" && (
                <StyledWrapper
                    {...styles.centeringWrapper}
                    onClick={imageWrapperClickHandler}
                    className={errorLoadingImage ? "has-error" : ""}
                >
                    <Img
                        {...styles.img}
                        src={path}
                        altText={image.imageAltText}
                        loading="eager"
                        data-test-selector="productDetails_mainImage"
                        onKeyPress={imageWrapperKeyHandler}
                        tabIndex={0}
                    />
                </StyledWrapper>
            )}
            {image.imageType === "360" && productSettings.imageProvider === "SIRV" && (
                <div style={{ minHeight: 300 }} onClick={imageWrapperClickHandler}>
                    <div className="Sirv" key={image.id} data-src={image.mediumImagePath} />
                </div>
            )}
        </>
    );
};

export default connect(mapStateToProps)(ProductPrimaryImage);
